import {
  ApiError,
  api,
  bounded,
  noteSignedInRecorded,
  signOutCount,
  signedOutOnThisPage,
} from '../api/client.js';
import { rememberSignedIn, signedInHere } from './db.js';

/** Long enough for a slow gym link, short enough that a back control always appears. Also the `/me` deadline (ADR-0085). */
export const ROLE_READ_TIMEOUT_MS = 4_000;

/**
 * Who may open a book on this phone (#43, ADR-0090).
 *
 * A book holds a roster of children's names and numbers, and plays recorded
 * into it are sent under whoever is signed in. So a book opens only for the
 * person who set it up, judged against what THIS DEVICE last knew about who
 * is signed in, because the book has to open with no signal and a network
 * check would block exactly the cold start the scorebook promises.
 *
 * `signedIn` is a user id, `null` after a sign-out on this phone, or
 * `undefined` on a phone that has recorded neither (one in use before #43).
 */
export type SignedInHere = string | null | undefined;

export function mayOpen(bookUserId: string | undefined, signedIn: SignedInHere): boolean {
  // A book cached before books carried an owner: refusing it would strand a
  // game in progress, so only a sign-out since then closes it.
  if (bookUserId === undefined) return signedIn !== null;
  return signedIn === bookUserId;
}

/**
 * Ask the server who is signed in, and record the answer on this phone.
 *
 * Called on every successful sign-in and wherever the app asks `/api/auth/me`,
 * so the phone's record names the person before they lose signal: without it
 * a coach who signed out and back in was refused their own book offline.
 *
 * A definite "no session" records nobody, so a session that ran out without a
 * sign-out closes the book to the next person exactly as a sign-out does.
 *
 * Never throws. Resolves to the user id the server named, `null` when it said
 * there is no session, or `undefined` when it said nothing this can act on.
 */
export async function confirmSignedIn(): Promise<SignedInHere> {
  const asked = signOutCount();
  let userId: string | null;
  try {
    // Bounded (ADR-0085): every sign-in waits on this before it navigates.
    const me = await api<{ user?: { id?: unknown } }>(
      '/api/auth/me',
      {},
      { timeoutMs: ROLE_READ_TIMEOUT_MS },
    );
    if (typeof me.user?.id !== 'string') return undefined;
    userId = me.user.id;
  } catch (error) {
    // No signal, a portal page, a server fault: the phone's last record stands.
    if (!(error instanceof ApiError) || !NO_SESSION.has(error.code)) return undefined;
    userId = null;
  }
  // Asked before a sign-out on this page: it names whoever was here then, not now.
  if (signOutCount() !== asked) return undefined;
  // The server's answer stands even if the phone cannot write it down, or never answers:
  // every sign-in waits on this before it navigates.
  const stored = await bounded(rememberSignedIn(userId).then(() => true));
  // Only a record the store took reopens books on a page that signed out (#43).
  if (stored === true && userId !== null && signOutCount() === asked) noteSignedInRecorded();
  return userId;
}

/** The answers that mean the server knows nobody is signed in on this phone. */
const NO_SESSION: ReadonlySet<string> = new Set(['AUTH_REQUIRED', 'AUTH_SESSION_EXPIRED']);

/**
 * Who a cached book is judged against on this page (#43, N1): nobody after a
 * sign-out here, until the store records a sign-in here, whatever it still says. A full
 * store refuses the sign-out's write but still reads, so its record can go on
 * naming the coach. Rejects when the store does, like `signedInHere`.
 */
export async function signedInForBooks(): Promise<SignedInHere> {
  return signedOutOnThisPage() ? null : signedInHere();
}

/**
 * After a sign-out lands: clear this phone's record of who was signed in,
 * trying twice. Only the record, because writing the pending sign-out again
 * would end whoever signs in next. True once the store has taken it.
 */
export async function clearSignedIn(): Promise<boolean> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if ((await bounded(rememberSignedIn(null).then(() => true))) === true) return true;
  }
  return false;
}
